import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Phone, Mail, Headphones } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keywords that trigger live agent transfer
const AGENT_KEYWORDS = [
  'refund', 'cancel', 'cancellation', 'cancelled', 'money back',
  'complaint', 'complain', 'unhappy', 'disappointed', 'angry',
  'speak to someone', 'talk to someone', 'real person', 'human',
  'manager', 'supervisor', 'live agent', 'live chat', 'support agent'
];

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAgentOptions, setShowAgentOptions] = useState(false);
  const [showLiveAgentCard, setShowLiveAgentCard] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! 👋 I'm Reva, your dining assistant.\n\nHow can I help you today?",
      id: Date.now()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  // Quick action suggestions
  const quickActions = [
    { label: "📋 View Menu", action: "menu" },
    { label: "📅 Book a Table", action: "booking" },
    { label: "🕐 Opening Hours", action: "hours" },
    { label: "📍 Location", action: "location" }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if message contains keywords that need live agent
  const needsLiveAgent = (message) => {
    const lowerMessage = message.toLowerCase();
    return AGENT_KEYWORDS.some(keyword => lowerMessage.includes(keyword));
  };

  // Open Tawk.to live chat
  const openTawkChat = () => {
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
    } else {
      // Fallback if Tawk isn't loaded
      window.open('https://wa.me/447837052783?text=Hello!%20I%20need%20assistance%20with%20my%20inquiry%20at%20Reva%20Dining.', '_blank');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, id: Date.now() }]);
    setLoading(true);
    setShowAgentOptions(false);
    setShowLiveAgentCard(false);

    // Check if message needs live agent
    if (needsLiveAgent(userMessage)) {
      setLoading(false);
      setShowLiveAgentCard(true);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'agent_transfer_card',
        type: 'agent_transfer_card',
        id: Date.now() + 1
      }]);
      return;
    }

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response, id: Date.now() + 1 }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologise, but I'm having trouble responding right now. Please try again or contact us directly at bookings@revadining.co.uk or call +44 20 8892 9841.",
        id: Date.now() + 1
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferToAgent = () => {
    setShowAgentOptions(false);
    setShowLiveAgentCard(true);
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: 'agent_transfer_card',
      type: 'agent_transfer_card',
      id: Date.now()
    }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button - Reva AI Assistant */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-56 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-600 hover:bg-gray-700' 
            : 'bg-malva hover:bg-malva-dark'
        }`}
        data-testid="chatbot-toggle"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-72 right-6 z-50 w-[360px] h-[480px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-malva to-malva-dark p-4 text-white">
            <h3 className="font-serif text-lg">Chat with Reva</h3>
            <p className="text-white/80 text-sm">Your virtual dining assistant</p>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id || `${message.role}-${message.content.substring(0, 20)}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {/* Special Card Types */}
                {message.type === 'menu_card' ? (
                  <div className="max-w-[90%] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-malva/10 to-malva/5 p-3 border-b border-gray-100">
                      <h4 className="font-serif text-gray-800 font-medium">Our Menu</h4>
                    </div>
                    <div className="p-3 space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Explore our exquisite selection:</p>
                      <div className="grid grid-cols-2 gap-1.5 text-xs">
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Cold Entrees</span>
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Hot Entrees</span>
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Main Courses</span>
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Grills</span>
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Reva Steaks</span>
                        <span className="bg-malva/5 text-gray-700 px-2 py-1.5 rounded border border-malva/10">Breads & Rice</span>
                      </div>
                      <a 
                        href="/menu" 
                        className="block w-full mt-3 px-4 py-2 bg-malva text-white text-center text-sm rounded-lg hover:bg-malva-dark transition-colors"
                      >
                        View Full Menu
                      </a>
                    </div>
                  </div>
                ) : message.type === 'booking_card' ? (
                  <div className="max-w-[90%] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-malva/10 to-malva/5 p-3 border-b border-gray-100">
                      <h4 className="font-serif text-gray-800 font-medium">Reserve a Table</h4>
                    </div>
                    <div className="p-3 space-y-3">
                      <p className="text-sm text-gray-600">Book your dining experience:</p>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>✓ Tables for 2-10 guests</p>
                        <p>✓ Special occasions welcome</p>
                        <p>✓ Instant confirmation</p>
                      </div>
                      <a 
                        href="/booking" 
                        className="block w-full px-4 py-2 bg-malva text-white text-center text-sm rounded-lg hover:bg-malva-dark transition-colors"
                      >
                        Book Now
                      </a>
                      <p className="text-xs text-gray-400 text-center">Or call +44 20 8892 9841</p>
                    </div>
                  </div>
                ) : message.type === 'hours_card' ? (
                  <div className="max-w-[90%] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-malva/10 to-malva/5 p-3 border-b border-gray-100">
                      <h4 className="font-serif text-gray-800 font-medium">Opening Hours</h4>
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-xs font-medium text-gray-700">Lunch</span>
                          <span className="text-xs text-gray-600">12:00 PM - 3:00 PM</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-xs font-medium text-gray-700">Dinner</span>
                          <span className="text-xs text-gray-600">5:00 PM - 10:30 PM</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-xs font-medium text-gray-700">Days</span>
                          <span className="text-xs text-malva font-medium">Mon - Sun</span>
                        </div>
                      </div>
                      <div className="bg-green-50 text-green-700 text-xs px-3 py-2 rounded-lg text-center">
                        Open 7 days a week
                      </div>
                      <a 
                        href="/booking" 
                        className="block w-full px-4 py-2 bg-malva text-white text-center text-sm rounded-lg hover:bg-malva-dark transition-colors"
                      >
                        Make a Reservation
                      </a>
                    </div>
                  </div>
                ) : message.type === 'location_card' ? (
                  <div className="max-w-[90%] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-malva/10 to-malva/5 p-3 border-b border-gray-100">
                      <h4 className="font-serif text-gray-800 font-medium">Find Us</h4>
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-800">Reva Dining</p>
                        <p className="text-xs text-gray-600 mt-1">45-47 York St, Twickenham</p>
                        <p className="text-xs text-gray-600">TW1 3LP, United Kingdom</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <a href="tel:+442088929841" className="flex items-center justify-center gap-1.5 bg-malva/5 text-gray-700 text-xs px-2 py-2 rounded-lg hover:bg-malva/10 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                          Call Us
                        </a>
                        <a href="mailto:bookings@revadining.co.uk" className="flex items-center justify-center gap-1.5 bg-malva/5 text-gray-700 text-xs px-2 py-2 rounded-lg hover:bg-malva/10 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          Email Us
                        </a>
                      </div>
                      <a 
                        href="https://maps.google.com/?q=45-47+York+St,+Twickenham,+TW1+3LP" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-4 py-2 bg-gray-700 text-white text-center text-sm rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Get Directions
                      </a>
                    </div>
                  </div>
                ) : message.type === 'agent_transfer_card' ? (
                  <div className="max-w-[90%] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500/10 to-blue-400/5 p-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4 text-blue-600" />
                        <h4 className="font-serif text-gray-800 font-medium">Connect with Live Agent</h4>
                      </div>
                    </div>
                    <div className="p-3 space-y-3">
                      <p className="text-sm text-gray-600">I'll connect you with our next available team member who can help with:</p>
                      <div className="text-xs text-gray-500 space-y-1 bg-gray-50 p-2 rounded-lg">
                        <p>• Booking modifications & cancellations</p>
                        <p>• Refunds & payment inquiries</p>
                        <p>• Special requests & complaints</p>
                      </div>
                      <div className="pt-2 space-y-2">
                        <button
                          onClick={openTawkChat}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Headphones className="w-4 h-4" />
                          Start Live Chat
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          <a 
                            href="https://wa.me/447837052783?text=Hello!%20I%20need%20assistance%20with%20my%20booking%20at%20Reva%20Dining."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-xs rounded-lg hover:bg-[#128C7E] transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            WhatsApp
                          </a>
                          <a 
                            href="tel:+442088929841"
                            className="flex items-center justify-center gap-1.5 px-3 py-2 bg-malva text-white text-xs rounded-lg hover:bg-malva-dark transition-colors"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Call Now
                          </a>
                        </div>
                        <a 
                          href="mailto:bookings@revadining.co.uk?subject=Support%20Request"
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          Email Support
                        </a>
                      </div>
                      <p className="text-xs text-gray-400 text-center">Average response time: Under 5 minutes</p>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-malva text-white rounded-br-md'
                        : 'bg-white text-gray-700 rounded-bl-md shadow-sm border border-gray-100'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                )}
              </div>
            ))}
            
            {/* Quick Action Buttons - Always visible for easy navigation */}
            {!loading && (
              <div className="sticky bottom-0 bg-gray-50 pt-2 pb-1">
                <p className="text-xs text-gray-400 text-center mb-2">Quick actions:</p>
                <div className="grid grid-cols-2 gap-2 px-2">
                  {quickActions.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => {
                        if (item.action === 'menu') {
                          setMessages(prev => [...prev, 
                            { role: 'user', content: 'Show me the menu', id: Date.now() },
                            { role: 'assistant', content: 'menu_card', id: Date.now() + 1, type: 'menu_card' }
                          ]);
                        } else if (item.action === 'booking') {
                          setMessages(prev => [...prev,
                            { role: 'user', content: 'I want to book a table', id: Date.now() },
                            { role: 'assistant', content: 'booking_card', id: Date.now() + 1, type: 'booking_card' }
                          ]);
                        } else if (item.action === 'hours') {
                          setMessages(prev => [...prev,
                            { role: 'user', content: 'What are your opening hours?', id: Date.now() },
                            { role: 'assistant', content: 'hours_card', id: Date.now() + 1, type: 'hours_card' }
                          ]);
                        } else if (item.action === 'location') {
                          setMessages(prev => [...prev,
                            { role: 'user', content: 'Where are you located?', id: Date.now() },
                            { role: 'assistant', content: 'location_card', id: Date.now() + 1, type: 'location_card' }
                          ]);
                        }
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-xs bg-white text-gray-700 rounded-lg hover:bg-malva/10 transition-colors border border-gray-200 hover:border-malva/30 shadow-sm"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Agent Contact Options */}
            {showAgentOptions && (
              <div className="flex justify-start">
                <div className="bg-white p-4 rounded-2xl rounded-bl-md shadow-sm border border-gray-100 space-y-3">
                  <a 
                    href="https://wa.me/447837052783?text=Hello!%20I%20need%20assistance%20with%20my%20inquiry%20at%20Reva%20Dining."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg transition-colors"
                    data-testid="chatbot-whatsapp"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="text-sm font-medium">WhatsApp</span>
                  </a>
                  <a 
                    href="tel:+442088929841"
                    className="flex items-center gap-3 px-4 py-2 bg-malva hover:bg-malva-dark text-white rounded-lg transition-colors"
                    data-testid="chatbot-call"
                  >
                    <Phone className="w-5 h-5" />
                    <span className="text-sm font-medium">Call Us</span>
                  </a>
                  <a 
                    href="mailto:bookings@revadining.co.uk"
                    className="flex items-center gap-3 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    data-testid="chatbot-email"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="text-sm font-medium">Email Us</span>
                  </a>
                </div>
              </div>
            )}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-700 p-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <Loader2 className="w-5 h-5 animate-spin text-malva" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Transfer to Agent Button */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleTransferToAgent}
              className="w-full text-sm text-malva-dark hover:text-malva-darker font-medium py-1 transition-colors"
              data-testid="chatbot-transfer-agent"
            >
              Not satisfied? Talk to our team
            </button>
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-malva focus:ring-1 focus:ring-malva"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="w-10 h-10 bg-malva hover:bg-malva-dark disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
